type AuthPayload = {
  token: string;
  userId: string;
  name?: string;
  email?: string;
  role?: string;
};

type AccessTokenClaims = {
  accountOwnerId?: string;
  exp?: number;
};

const PARENT_AUTH_COOKIE_NAME = 'sessionmonkey_parent_auth';
const PUBLIC_ROOT_DOMAIN = (process.env.REACT_APP_PUBLIC_ROOT_DOMAIN || 'sessionmonkey.com').toLowerCase();

const readCookieValue = (name: string): string | null => {
  const encodedName = `${encodeURIComponent(name)}=`;
  const segments = document.cookie.split(';');
  for (const segment of segments) {
    const trimmed = segment.trim();
    if (trimmed.startsWith(encodedName)) {
      return decodeURIComponent(trimmed.slice(encodedName.length));
    }
  }
  return null;
};

const resolveCookieDomain = (): string | null => {
  const host = String(window.location.hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host.endsWith('.localhost')) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
  if (host === PUBLIC_ROOT_DOMAIN || host.endsWith(`.${PUBLIC_ROOT_DOMAIN}`)) {
    return `.${PUBLIC_ROOT_DOMAIN}`;
  }
  return null;
};

const writeCookie = (name: string, value: string, expiresAt: Date | null) => {
  const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = resolveCookieDomain();
  const domainPart = domain ? `; Domain=${domain}` : '';
  const expiresPart = expiresAt ? `; Expires=${expiresAt.toUTCString()}` : '';
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/${expiresPart}; SameSite=Lax${secureFlag}${domainPart}`;
};

const deleteCookie = (name: string) => {
  const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
  const expiresPart = '; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const domain = resolveCookieDomain();
  const domainPart = domain ? `; Domain=${domain}` : '';
  document.cookie = `${encodeURIComponent(name)}=; Path=/${expiresPart}; SameSite=Lax${secureFlag}`;
  if (domainPart) {
    document.cookie = `${encodeURIComponent(name)}=; Path=/${expiresPart}; SameSite=Lax${secureFlag}${domainPart}`;
  }
};

const decodeBase64Url = (value: string): string | null => {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return window.atob(padded);
  } catch {
    return null;
  }
};

const getAccessTokenClaims = (token?: string | null): AccessTokenClaims | null => {
  const rawToken = String(token || '').trim();
  if (!rawToken) return null;

  const [, encodedPayload] = rawToken.split('.');
  if (!encodedPayload) return null;

  try {
    const decoded = decodeBase64Url(encodedPayload);
    if (!decoded) return null;
    return JSON.parse(decoded) as AccessTokenClaims;
  } catch {
    return null;
  }
};

const persistToLocalStorage = (payload: AuthPayload) => {
  localStorage.setItem('token', payload.token);
  localStorage.setItem('userId', payload.userId);
  if (payload.name) localStorage.setItem('name', payload.name);
  if (payload.email) localStorage.setItem('email', payload.email);
  if (payload.role) localStorage.setItem('role', payload.role);
};

const saveAuthPayload = (payload: AuthPayload) => {
  persistToLocalStorage(payload);
  if (payload.role === 'parent') {
    writeCookie(PARENT_AUTH_COOKIE_NAME, JSON.stringify(payload), null);
  } else {
    deleteCookie(PARENT_AUTH_COOKIE_NAME);
  }
};

const loadParentAuthPayloadFromCookie = (): AuthPayload | null => {
  const value = readCookieValue(PARENT_AUTH_COOKIE_NAME);
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<AuthPayload>;
    if (!parsed?.token || !parsed?.userId || parsed.role !== 'parent') return null;
    return {
      token: parsed.token,
      userId: parsed.userId,
      name: parsed.name,
      email: parsed.email,
      role: parsed.role,
    };
  } catch {
    return null;
  }
};

const getCurrentAuthPayload = (): AuthPayload | null => {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  if (token && userId) {
    return {
      token,
      userId,
      name: localStorage.getItem('name') || undefined,
      email: localStorage.getItem('email') || undefined,
      role: localStorage.getItem('role') || undefined,
    };
  }

  const fromCookie = loadParentAuthPayloadFromCookie();
  if (fromCookie) {
    persistToLocalStorage(fromCookie);
    return fromCookie;
  }

  return null;
};

// Auth utilities with localStorage + shared cookie fallback for subdomains.
export const auth = {
  getToken: (): string | null => {
    const payload = getCurrentAuthPayload();
    return payload?.token || null;
  },

  getUser: () => {
    const payload = getCurrentAuthPayload();
    if (payload) {
      const accessTokenClaims = getAccessTokenClaims(payload.token);
      const accountOwnerId = accessTokenClaims?.accountOwnerId || undefined;
      const isOrganizationOwner = payload.role === 'parent'
        ? false
        : (accountOwnerId ? payload.userId === accountOwnerId : true);
      return {
        token: payload.token,
        userId: payload.userId,
        name: payload.name,
        email: payload.email,
        role: payload.role,
        accountOwnerId,
        isOrganizationOwner,
        isAuthenticated: true,
      };
    }
    return {
      token: undefined,
      userId: undefined,
      name: undefined,
      email: undefined,
      role: undefined,
      accountOwnerId: undefined,
      isOrganizationOwner: false,
      isAuthenticated: false,
    };
  },

  setAuth: (token: string, userId: string, name?: string, email?: string, role?: string) => {
    saveAuthPayload({ token, userId, name, email, role });
    window.dispatchEvent(new Event('clubsbooking-auth-updated'));
  },

  clearAuth: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
    deleteCookie(PARENT_AUTH_COOKIE_NAME);
    window.dispatchEvent(new Event('clubsbooking-auth-updated'));
  },

  isAuthenticated: (): boolean => {
    const payload = getCurrentAuthPayload();
    return !!(payload?.token && payload?.userId);
  },

  isTokenExpired: (token?: string | null, skewSeconds = 15): boolean => {
    const claims = getAccessTokenClaims(token ?? getCurrentAuthPayload()?.token);
    if (!claims?.exp) return false;
    const nowInSeconds = Math.floor(Date.now() / 1000);
    return claims.exp <= nowInSeconds + Math.max(0, skewSeconds);
  },
};
